import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { GeocodeResponse, MarketsResponse, WeatherResponse, WidgetLocation } from '@omnia/shared';
import { api, shouldRetry } from '../lib/api.ts';

export const WEATHER_REFRESH_MS = 10 * 60_000;
export const MARKETS_REFRESH_MS = 3 * 60_000;

/** A value the server flagged stale, or one that is older than the last successful refresh. */
export interface Freshness {
  stale: boolean;
}

// refetchInterval pauses while the tab is hidden (refetchIntervalInBackground defaults to false).
export function useWeather(location: WidgetLocation, enabled: boolean) {
  const lat = location.lat.toFixed(2);
  const lon = location.lon.toFixed(2);
  const query = useQuery({
    queryKey: ['widget', 'weather', lat, lon],
    queryFn: ({ signal }) => api<WeatherResponse>(`/api/widgets/weather?lat=${lat}&lon=${lon}`, { signal }),
    enabled,
    retry: shouldRetry,
    refetchInterval: WEATHER_REFRESH_MS,
    staleTime: WEATHER_REFRESH_MS / 2,
    placeholderData: keepPreviousData,
  });
  return { ...query, stale: Boolean(query.data?.stale) || (query.isError && query.data !== undefined) };
}

export function useMarkets(enabled: boolean) {
  const query = useQuery({
    queryKey: ['widget', 'markets'],
    queryFn: ({ signal }) => api<MarketsResponse>('/api/widgets/markets', { signal }),
    enabled,
    retry: shouldRetry,
    refetchInterval: MARKETS_REFRESH_MS,
    staleTime: MARKETS_REFRESH_MS / 2,
  });
  return { ...query, stale: Boolean(query.data?.stale) || (query.isError && query.data !== undefined) };
}

export function useGeocode(text: string) {
  const q = text.trim();
  return useQuery({
    queryKey: ['widget', 'geocode', q.toLowerCase()],
    queryFn: ({ signal }) => api<GeocodeResponse>(`/api/widgets/geocode?q=${encodeURIComponent(q)}`, { signal }),
    enabled: q.length >= 2,
    retry: shouldRetry,
    staleTime: 60 * 60_000,
    placeholderData: keepPreviousData,
  });
}
